import React from 'react';
import {
    Building2, RefreshCw, Loader, CheckCircle, XCircle,
    Clock, PlayCircle, PowerOff, Save, X
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';

const DAY_OPTIONS = [
    { value: 0, label: 'Monday' },
    { value: 1, label: 'Tuesday' },
    { value: 2, label: 'Wednesday' },
    { value: 3, label: 'Thursday' },
    { value: 4, label: 'Friday' },
    { value: 5, label: 'Saturday' },
    { value: 6, label: 'Sunday' },
];

const formatHour = (h) => {
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:00 ${ampm} UTC`;
};

export default function RescrapeTab({
    rescrapeData,
    reloadRatesLoading,
    rescrapingEmployerId,
    // Set interval modal
    showSetIntervalModal,
    setShowSetIntervalModal,
    intervalEmployer,
    selectedInterval,
    setSelectedInterval,
    selectedDay,
    setSelectedDay,
    selectedHour,
    setSelectedHour,
    selectedMaxPages,
    setSelectedMaxPages,
    settingInterval,
    onSaveInterval,
    onOpenSetInterval,
    // Actions
    onRunNow,
    onDisableSchedule,
    onRefresh,
}) {
    const { isDarkMode } = useTheme();

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '—';
        try {
            return new Date(dateStr).toLocaleString();
        } catch {
            return '—';
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Auto-Reload Schedules</h2>
                <button
                    onClick={onRefresh}
                    className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors flex items-center gap-2"
                >
                    <RefreshCw size={18} />
                    Refresh
                </button>
            </div>

            {reloadRatesLoading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader className="animate-spin text-primary-600" size={32} />
                </div>
            ) : (
                <div className={`rounded-xl shadow-sm border overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                    <table className="w-full">
                        <thead className={`border-b ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                            <tr>
                                <th className={`text-left px-6 py-4 text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Employer</th>
                                <th className={`text-left px-6 py-4 text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Interval</th>
                                <th className={`text-left px-6 py-4 text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Next Run</th>
                                <th className={`text-left px-6 py-4 text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Last Run</th>
                                <th className={`text-left px-6 py-4 text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Status</th>
                                <th className={`text-left px-6 py-4 text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Actions</th>
                            </tr>
                        </thead>
                        <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-slate-100'}`}>
                            {rescrapeData.map(employer => (
                                <tr key={employer.EmployerID} className={`transition-colors ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}`}>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <Building2 className={isDarkMode ? 'text-slate-500' : 'text-slate-400'} size={20} />
                                            <span className={`font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{employer.Name}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                            employer.interval === 'None'
                                                ? 'bg-slate-100 text-slate-700'
                                                : 'bg-primary-100 text-primary-700'
                                        }`}>
                                            {employer.interval}
                                        </span>
                                    </td>
                                    <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                        {formatDateTime(employer.next_run_at)}
                                    </td>
                                    <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                        {formatDateTime(employer.last_ran_at)}
                                    </td>
                                    <td className="px-6 py-4">
                                        {employer.last_status === 'OK' && (
                                            <span className="flex items-center gap-1 text-green-600 text-sm">
                                                <CheckCircle size={16} />
                                                OK
                                            </span>
                                        )}
                                        {employer.last_status === 'FAILED' && (
                                            <span className="flex items-center gap-1 text-red-600 text-sm" title={employer.last_error}>
                                                <XCircle size={16} />
                                                Failed
                                            </span>
                                        )}
                                        {employer.last_status === 'RUNNING' && (
                                            <span className="flex items-center gap-1 text-blue-600 text-sm">
                                                <Loader className="animate-spin" size={16} />
                                                Running
                                            </span>
                                        )}
                                        {!employer.last_status && (
                                            <span className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>—</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => onOpenSetInterval(employer)}
                                                className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-slate-400 hover:text-primary-400 hover:bg-slate-700' : 'text-slate-600 hover:text-primary-600 hover:bg-primary-50'}`}
                                                title="Set Interval"
                                            >
                                                <Clock size={18} />
                                            </button>
                                            {employer.WorkdayURL && (
                                                <button
                                                    onClick={() => onRunNow(employer)}
                                                    disabled={rescrapingEmployerId === employer.EmployerID}
                                                    className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${isDarkMode ? 'text-slate-400 hover:text-blue-400 hover:bg-slate-700' : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'}`}
                                                    title="Run Now"
                                                >
                                                    {rescrapingEmployerId === employer.EmployerID ? (
                                                        <Loader className="animate-spin" size={18} />
                                                    ) : (
                                                        <PlayCircle size={18} />
                                                    )}
                                                </button>
                                            )}
                                            {employer.is_active && (
                                                <button
                                                    onClick={() => onDisableSchedule(employer)}
                                                    className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-slate-400 hover:text-red-400 hover:bg-slate-700' : 'text-slate-600 hover:text-red-600 hover:bg-red-50'}`}
                                                    title="Disable"
                                                >
                                                    <PowerOff size={18} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Set Interval Modal */}
            {showSetIntervalModal && intervalEmployer && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className={`rounded-2xl shadow-2xl max-w-md w-full ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                        <div className={`p-6 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <div className="flex items-center justify-between">
                                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Set Rescrape Interval</h2>
                                <button
                                    onClick={() => setShowSetIntervalModal(false)}
                                    className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100'}`}
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                    Set automatic rescrape interval for <strong className={isDarkMode ? 'text-slate-200' : ''}>{intervalEmployer.Name}</strong>
                                </p>
                                <select
                                    value={selectedInterval}
                                    onChange={(e) => setSelectedInterval(e.target.value)}
                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black'}`}
                                >
                                    <option value="None">None</option>
                                    <option value="Hourly">Hourly</option>
                                    <option value="Daily">Daily</option>
                                    <option value="Weekly">Weekly</option>
                                    <option value="Biweekly">Biweekly</option>
                                    <option value="Monthly">Monthly</option>
                                    <option value="Custom Weekly">Custom Weekly</option>
                                </select>

                                {selectedInterval === 'Custom Weekly' && (
                                    <div className="mt-3 space-y-3">
                                        <div>
                                            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Day of Week</label>
                                            <select
                                                value={selectedDay}
                                                onChange={(e) => setSelectedDay(parseInt(e.target.value))}
                                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black'}`}
                                            >
                                                {DAY_OPTIONS.map(d => (
                                                    <option key={d.value} value={d.value}>{d.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Time (UTC)</label>
                                            <select
                                                value={selectedHour}
                                                onChange={(e) => setSelectedHour(parseInt(e.target.value))}
                                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black'}`}
                                            >
                                                {Array.from({ length: 24 }, (_, h) => (
                                                    <option key={h} value={h}>{formatHour(h)}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Max Pages Per Scrape</label>
                                <input
                                    type="number"
                                    min={1}
                                    max={20}
                                    value={selectedMaxPages ?? ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        let parsed = parseInt(value, 10);
                                        if (isNaN(parsed)) {
                                            setSelectedMaxPages('');
                                            return;
                                        }
                                        parsed = Math.max(1, Math.min(20, parsed));
                                        setSelectedMaxPages(parsed);
                                    }}
                                    placeholder="1-20"
                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400' : 'border-slate-300 text-black'}`}
                                />
                                <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Pick a number between 1 and 20</p>
                            </div>
                        </div>

                        <div className={`p-6 border-t flex items-center justify-end gap-3 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <button
                                onClick={() => setShowSetIntervalModal(false)}
                                className={`px-6 py-2 border rounded-lg transition-colors ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-700 hover:bg-slate-50'}`}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={onSaveInterval}
                                disabled={settingInterval}
                                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                            >
                                {settingInterval ? (
                                    <>
                                        <Loader className="animate-spin" size={18} />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save size={18} />
                                        Save
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}